import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SiteNav } from './SiteNav';
import { Footer } from './Footer';
import { CheckCircle2, FileText, Clock, Mail, ArrowRight, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 10; // Try up to 10 times (20 seconds total)

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/intake/confirmation/${sessionId}`);
        if (response.data && response.data.intake_reference_id) {
          setOrderDetails(response.data);
          setLoading(false);
        } else {
          throw new Error('Order not ready');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        
        // If we haven't exhausted retries and it's a 404 (order not found yet), retry
        if (retryCount < maxRetries && (err.response?.status === 404 || err.message === 'Order not ready')) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000); // Retry every 2 seconds
        } else {
          setError('Unable to fetch order details. Your payment was successful - please check your email for confirmation.');
          setLoading(false);
        }
      }
    };

    fetchOrderDetails();
  }, [sessionId, retryCount]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--color-navy)', borderTopColor: 'transparent' }}></div>
          <p style={{ color: 'var(--color-muted)' }}>
            {retryCount > 0 
              ? `Processing your order... (${retryCount}/${maxRetries})`
              : 'Loading your order details...'
            }
          </p>
          {retryCount > 3 && (
            <p className="text-sm mt-2" style={{ color: 'var(--color-muted)' }}>
              This may take a moment while we confirm your payment.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg)' }} data-testid="confirmation-page">
      {/* Navigation */}
      <SiteNav cta="home" />

      {/* Main Content */}
      <main className="flex-1 px-6 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#059669' }}>
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--color-navy)' }} data-testid="confirmation-title">
              Payment Confirmed
            </h1>
            <p className="text-lg" style={{ color: 'var(--color-muted)' }}>
              Thank you for your order. Your Schedule of Works is now in production.
            </p>
          </div>

          {/* Order Reference Card */}
          {orderDetails && (
            <div className="card p-8 mb-8" data-testid="order-reference-card">
              <div className="text-center mb-6 pb-6" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <p className="section-label mb-2">YOUR ORDER REFERENCE</p>
                <p className="text-3xl md:text-4xl font-bold font-mono tracking-wide" style={{ color: 'var(--color-navy)' }} data-testid="order-reference-id">
                  {orderDetails.intake_reference_id}
                </p>
                <p className="text-sm mt-2" style={{ color: 'var(--color-muted)' }}>
                  Please save this reference for your records
                </p>
              </div>

              {/* Order Details Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-bg)' }}>
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Project Type</p>
                    <p style={{ color: 'var(--color-muted)' }}>{orderDetails.project_type || 'Standard Project'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-bg)' }}>
                    <Clock className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Expected Delivery</p>
                    <p style={{ color: '#059669', fontWeight: '600' }}>{orderDetails.delivery_date_long || 'To be confirmed'}</p>
                    {orderDetails.delivery_note && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>{orderDetails.delivery_note}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-bg)' }}>
                    <Mail className="w-5 h-5" style={{ color: 'var(--color-navy)' }} />
                  </div>
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Confirmation Email</p>
                    <p style={{ color: 'var(--color-muted)' }}>Sent to {orderDetails.client_email || 'your email'}</p>
                  </div>
                </div>

                {orderDetails.site_address && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-bg)' }}>
                      <svg className="w-5 h-5" style={{ color: 'var(--color-navy)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Site Address</p>
                      <p style={{ color: 'var(--color-muted)' }}>{orderDetails.site_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !orderDetails && (
            <div className="card p-8 mb-8 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#059669' }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-navy)' }}>Payment Successful</h2>
              <p style={{ color: 'var(--color-muted)' }}>
                Your payment has been processed. You will receive a confirmation email with your order reference shortly.
              </p>
            </div>
          )}

          {/* What Happens Next */}
          <div className="card p-8 mb-8" data-testid="next-steps-card">
            <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--color-navy)' }}>
              What happens next?
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ backgroundColor: 'var(--color-navy)' }}>1</div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Confirmation emails sent</p>
                  <p style={{ color: 'var(--color-muted)' }}>You'll receive a confirmation email with your order details and reference number.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ backgroundColor: 'var(--color-navy)' }}>2</div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Project review</p>
                  <p style={{ color: 'var(--color-muted)' }}>Our team will review your uploaded documents and project requirements.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ backgroundColor: 'var(--color-navy)' }}>3</div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Document preparation</p>
                  <p style={{ color: 'var(--color-muted)' }}>We'll prepare your professional Schedule of Works with detailed specifications.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ backgroundColor: 'var(--color-navy)' }}>4</div>
                <div>
                  <p className="font-semibold" style={{ color: 'var(--color-navy)' }}>Delivery</p>
                  <p style={{ color: 'var(--color-muted)' }}>Your completed documents will be delivered by the scheduled date. We'll contact you if we need any clarifications.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Download Receipt Button - prioritize custom receipt, fallback to Stripe invoice */}
            {(orderDetails?.receipt_url || orderDetails?.invoice_pdf_url) && (
              <a
                href={orderDetails.receipt_url || orderDetails.invoice_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-8 py-4 flex items-center justify-center gap-2"
                data-testid="download-receipt-btn"
                download={orderDetails.receipt_filename || 'receipt.pdf'}
              >
                <Download className="w-5 h-5" />
                Download Receipt
              </a>
            )}
            <button
              onClick={() => navigate('/')}
              className={`${(orderDetails?.receipt_url || orderDetails?.invoice_pdf_url) ? 'btn-secondary' : 'btn-primary'} px-8 py-4 flex items-center justify-center gap-2`}
              data-testid="confirmation-home-btn"
            >
              Return to Home
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="btn-secondary px-8 py-4"
              data-testid="confirmation-contact-btn"
            >
              Contact Us
            </button>
          </div>

          {/* Payment Amount Display */}
          {orderDetails?.total_amount > 0 && (
            <div className="text-center mt-4">
              <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                Amount paid: <span className="font-semibold" style={{ color: 'var(--color-navy)' }}>£{orderDetails.total_amount.toFixed(2)}</span>
              </p>
            </div>
          )}

          {/* View Invoice Online Link - only show Stripe invoice if no custom receipt */}
          {!orderDetails?.receipt_url && orderDetails?.invoice_url && (
            <div className="text-center mt-4">
              <a
                href={orderDetails.invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm hover:underline"
                style={{ color: 'var(--color-navy)' }}
                data-testid="view-invoice-link"
              >
                View invoice online →
              </a>
            </div>
          )}

          {/* Support Note */}
          <div className="text-center mt-8 pt-8" style={{ borderTop: '1px solid var(--color-border)' }}>
            <p style={{ color: 'var(--color-muted)' }}>
              Questions about your order? Contact us at{' '}
              <a href="mailto:team@schedulesofwork.com" className="font-medium hover:underline" style={{ color: 'var(--color-navy)' }}>
                team@schedulesofwork.com
              </a>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
